import type { ReactNode } from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  CircularProgress,
  Skeleton,
  Tooltip,
  Typography,
} from "@avenue-8/sidekick-ui";
import { LinearProgress } from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import FacebookIcon from "@mui/icons-material/Facebook";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { FormattedDate } from "@shared/components/FormattedDate/FormattedDate";
import { SuggestedApproachCard } from "@modules/radar/shared/components/SuggestedApproachCard";
import { useFindOneContact } from "@shared/generated/radar/queries/contactsController/useFindOneContact";
import { findOneContactQueryResponseSchema } from "@shared/generated/radar/zod/contactsController/findOneContactSchema";
import { isLikelyStillSyncing } from "@modules/radar/shared/helpers/isLikelyStillSyncing";
import { RadarPulseIcon } from "@modules/radar/shared/components/RadarPulseIcon/RadarPulseIcon";
import {
  radarSourceEnum,
  type RadarSourceKey,
} from "@modules/radar/radar.types";
import {
  toRadarContactSignalsViewModel,
  type RadarSignalEventViewModel,
  type RadarSignalSourceGroupViewModel,
  type RadarSignalsSummaryViewModel,
} from "./toRadarContactSignalsViewModel";

const SOURCE_ICONS: Record<RadarSourceKey, ReactNode> = {
  [radarSourceEnum.LINKEDIN]: <LinkedInIcon fontSize="small" />,
  [radarSourceEnum.INSTAGRAM]: <InstagramIcon fontSize="small" />,
  [radarSourceEnum.FACEBOOK]: <FacebookIcon fontSize="small" />,
  [radarSourceEnum.WEB_SEARCH]: <TravelExploreIcon fontSize="small" />,
  [radarSourceEnum.GMAIL]: <EmailOutlinedIcon fontSize="small" />,
};

const VISIBLE_EVENTS_PER_SOURCE = 10;
const SIGNALS_POLL_INTERVAL_MS = 15_000;

type RadarSignalsTabProps = {
  contactId: string;
  contactName: string;
  isMonitoredSince: string | null;
};

export const RadarSignalsTab = ({
  contactId,
  contactName,
  isMonitoredSince,
}: RadarSignalsTabProps) => {
  const contactQuery = useFindOneContact(
    contactId,
    { idType: "external" },
    undefined,
    {
      query: {
        select: (data) => findOneContactQueryResponseSchema.parse(data),
        refetchInterval: (query) => {
          const parsed = findOneContactQueryResponseSchema.safeParse(
            query.state.data,
          );
          if (parsed.success && parsed.data.events.length === 0) {
            return SIGNALS_POLL_INTERVAL_MS;
          }
          if (
            query.state.error?.status === 403 &&
            isLikelyStillSyncing(isMonitoredSince)
          ) {
            return SIGNALS_POLL_INTERVAL_MS;
          }
          return false;
        },
      },
    },
  );

  if (contactQuery.isLoading) {
    return (
      <Container>
        <Skeleton variant="rounded" height={120} />
        <Skeleton variant="rounded" height={200} />
      </Container>
    );
  }

  if (
    contactQuery.error?.status === 403 &&
    isLikelyStillSyncing(isMonitoredSince)
  ) {
    return (
      <Container>
        <EmptyStateCard>
          <EmptyStateIconWrap>
            <CircularProgress size={24} />
          </EmptyStateIconWrap>
          <MonitoringBadge>
            <Dot />
            <EyebrowLabel variant="overline" sx={{ marginLeft: "0.25rem" }}>
              Monitoring Active
            </EyebrowLabel>
          </MonitoringBadge>
          <Typography variant="subtitle1">Syncing with Radar</Typography>
          <Typography variant="body2" color="text.secondary">
            This can take a few minutes. Check back shortly.
          </Typography>
        </EmptyStateCard>
      </Container>
    );
  }

  if (contactQuery.isError || !contactQuery.data) {
    return (
      <Container>
        <EmptyStateCard>
          <EmptyStateIconWrap>
            <ErrorOutlineIcon fontSize="medium" />
          </EmptyStateIconWrap>
          <Typography variant="subtitle1">Something went wrong</Typography>
          <Typography variant="body2" color="text.secondary">
            We couldn&apos;t load Radar signals for this contact. Try refreshing
            the page.
          </Typography>
        </EmptyStateCard>
      </Container>
    );
  }

  const signals = toRadarContactSignalsViewModel(contactQuery.data);
  const aiSummarySection = signals.summary && (
    <AiSummarySection summary={signals.summary} />
  );

  return (
    <Container>
      {signals.totalSignals === 0 ? (
        <EmptyStateCard>
          <RadarPulseIcon />
          <MonitoringBadge>
            <Dot />
            <EyebrowLabel variant="overline" sx={{ marginLeft: "0.25rem" }}>
              Monitoring Active
            </EyebrowLabel>
          </MonitoringBadge>
          <Typography variant="subtitle1">No signals yet</Typography>
          <Typography variant="body2" color="text.secondary">
            Radar is building {contactName}&apos;s social and public activity
            history. Signals will appear here as soon as something happens.
          </Typography>
        </EmptyStateCard>
      ) : (
        <SummaryCard>
          <SummaryLayout>
            <SummaryTextColumn>
              <MonitoringBadge>
                <Dot />
                <EyebrowLabel variant="overline" sx={{ marginLeft: "0.25rem" }}>
                  Monitoring Active
                </EyebrowLabel>
              </MonitoringBadge>
              <Typography variant="subtitle1">
                All captured Radar signals
              </Typography>
              <Typography variant="body2">
                Complete social and public activity history for {contactName}.
              </Typography>
              {signals.summary?.executedAt && (
                <Typography variant="caption" color="text.secondary">
                  Last analyzed{" "}
                  <FormattedDate
                    value={signals.summary.executedAt}
                    relative
                    placement="top"
                  />
                </Typography>
              )}
            </SummaryTextColumn>
            <SummaryCount>
              <Typography variant="h4">{signals.totalSignals}</Typography>
              <Typography variant="caption" color="text.secondary">
                signals across {signals.sourceCount} sources
              </Typography>
            </SummaryCount>
          </SummaryLayout>
        </SummaryCard>
      )}

      {aiSummarySection}

      {signals.totalSignals > 0 &&
        signals.sources.map((source) => (
          <SourceCard key={source.label} source={source} />
        ))}
    </Container>
  );
};

const renderEventRow = (event: RadarSignalEventViewModel) => (
  <EventRow key={event.id}>
    <EventLabel>
      <EventDot />
      <Typography variant="body2" color="text.primary">
        {event.description}
      </Typography>
    </EventLabel>
    <EventTimestamp
      variant="body2"
      sx={!event.occurredAt ? { fontStyle: "italic", opacity: 0.7 } : undefined}
    >
      {event.occurredAt ? (
        <FormattedDate value={event.occurredAt} relative placement="left" />
      ) : (
        "No date"
      )}
    </EventTimestamp>
  </EventRow>
);

type SourceCardProps = {
  source: RadarSignalSourceGroupViewModel;
};

const SourceCard = ({ source }: SourceCardProps) => {
  const visibleEvents = source.events.slice(0, VISIBLE_EVENTS_PER_SOURCE);
  const remainingEvents = source.events.slice(VISIBLE_EVENTS_PER_SOURCE);

  return (
    <Card>
      <SourceHeader>
        <SourceIconWrap>
          {source.source ? (
            SOURCE_ICONS[source.source]
          ) : (
            <TravelExploreIcon fontSize="small" />
          )}
        </SourceIconWrap>
        <Typography variant="subtitle2">{source.label}</Typography>
        <EventCountChip label={`${source.events.length} events`} size="small" />
      </SourceHeader>
      <EventsList>{visibleEvents.map(renderEventRow)}</EventsList>
      {remainingEvents.length > 0 && (
        <ShowMoreAccordion disableGutters elevation={0}>
          <ShowMoreSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
            <Typography variant="body2">
              Show {remainingEvents.length} more
            </Typography>
          </ShowMoreSummary>
          <ShowMoreDetails>
            <EventsList>{remainingEvents.map(renderEventRow)}</EventsList>
          </ShowMoreDetails>
        </ShowMoreAccordion>
      )}
    </Card>
  );
};

type AiSummarySectionProps = {
  summary: RadarSignalsSummaryViewModel;
};

const AiSummarySection = ({ summary }: AiSummarySectionProps) => {
  const {
    score,
    summary: whyThisMatters,
    insights,
    suggestedApproach,
  } = summary;

  return (
    <Card>
      <PriorityLayout>
        <Tooltip
          title="Reflects urgency and likelihood of a meaningful opportunity, weighted against what matters most to you. Recent life events like relocations, family changes, or career moves count more than older or purely social activity."
          placement="bottom"
        >
          <ScoreColumn>
            <EyebrowLabel variant="overline">Priority Score</EyebrowLabel>
            {score !== null ? (
              <>
                <ScoreValueRow>
                  <ScoreValue>{score}</ScoreValue>
                  <ScoreMax>/100</ScoreMax>
                </ScoreValueRow>
                <LinearProgress
                  variant="determinate"
                  value={score}
                  sx={{ width: "100%", borderRadius: "0.25rem" }}
                />
                <Typography variant="body2">
                  High-priority relationship moment
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Not yet scored
              </Typography>
            )}
          </ScoreColumn>
        </Tooltip>
        <InsightsColumn>
          <WhyMattersColumn>
            {whyThisMatters && (
              <>
                <SectionHeading variant="subtitle2">
                  Why this matters
                </SectionHeading>
                <Typography variant="body2">{whyThisMatters}</Typography>
              </>
            )}
            {insights.length > 0 && (
              <>
                <SectionHeading variant="subtitle2" mt={1}>
                  Key insights
                </SectionHeading>
                <InsightsList>
                  {insights.map((insight, index) => (
                    <li key={index}>
                      <Typography variant="body2">{insight}</Typography>
                    </li>
                  ))}
                </InsightsList>
              </>
            )}
          </WhyMattersColumn>
          {suggestedApproach && (
            <SuggestedApproachCard text={suggestedApproach} size="md" />
          )}
        </InsightsColumn>
      </PriorityLayout>
    </Card>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Card = styled.div`
  border: 1px solid ${(p) => p.theme.colors.border2};
  border-radius: 0.5rem;
  padding: 1.5rem;
`;

const EmptyStateCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.5rem;
  padding: 4rem 1.5rem;
`;

const EmptyStateIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  margin-bottom: 0.5rem;
  border-radius: 50%;
  background-color: ${(p) => p.theme.colors.background5};
  color: ${(p) => p.theme.text.colors.brand};
`;

const SummaryCard = styled(Card)`
  background-color: ${(p) => p.theme.colors.background5};
`;

const PriorityLayout = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 1.5rem;

  ${(p) => p.theme.media.mobileDown} {
    grid-template-columns: 1fr;
  }
`;

const ScoreColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
`;

const ScoreValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
`;

const ScoreValue = styled.span`
  font-size: 3rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.success};
  line-height: 1;
`;

const ScoreMax = styled.span`
  font-size: 1rem;
  color: ${(p) => p.theme.text.colors.secondary};
`;

const InsightsColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  align-items: start;
  gap: 1.5rem;

  ${(p) => p.theme.media.laptopUp} {
    grid-template-columns: 2fr 1fr;
  }
`;

const WhyMattersColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SectionHeading = styled(Typography)`
  line-height: 1;
`;

const InsightsList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  color: ${(p) => p.theme.text.colors.secondary};
`;

const EyebrowLabel = styled(Typography)`
  font-weight: 700;
  line-height: 1.2;
`;

const SummaryLayout = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;

  ${(p) => p.theme.media.mobileDown} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const SummaryTextColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const MonitoringBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

const ping = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  100% {
    transform: scale(2.5);
    opacity: 0;
  }
`;

const Dot = styled.span`
  position: relative;
  display: inline-flex;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: ${(p) => p.theme.colors.success};

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background-color: ${(p) => p.theme.colors.success};
    animation: ${ping} 1.6s cubic-bezier(0, 0, 0.2, 1) infinite;
  }
`;

const SummaryCount = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background-color: ${(p) => p.theme.colors.background};
  border: 1px solid ${(p) => p.theme.colors.border2};
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  flex-shrink: 0;
`;

const SourceHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const SourceIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  background-color: ${(p) => p.theme.colors.background5};
  color: ${(p) => p.theme.text.colors.brand};
`;

const EventCountChip = styled(Chip)`
  background-color: ${(p) => p.theme.colors.background5};
  color: ${(p) => p.theme.text.colors.brand};
`;

const EventsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const EventRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.625rem 0;
  border-top: 1px solid ${(p) => p.theme.colors.border2};

  &:first-of-type {
    border-top: none;
  }

  ${(p) => p.theme.media.mobileDown} {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const EventLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EventTimestamp = styled(Typography)`
  flex-shrink: 0;
  min-width: 80px;
  text-align: right;
  white-space: nowrap;

  ${(p) => p.theme.media.mobileDown} {
    min-width: 0;
    text-align: left;
    margin-left: calc(0.375rem + 0.5rem);
  }
`;

const EventDot = styled.span`
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background-color: ${(p) => p.theme.text.colors.primary};
  flex-shrink: 0;
`;

const ShowMoreAccordion = styled(Accordion)`
  background-color: transparent;
  box-shadow: none;

  &::before {
    display: none;
  }
`;

const ShowMoreSummary = styled(AccordionSummary)`
  padding: 0;
  min-height: auto;

  & .MuiAccordionSummary-content {
    margin: 0.5rem 0;
  }
`;

const ShowMoreDetails = styled(AccordionDetails)`
  padding: 0;
`;
